import type {
  ShiftRecordMap,
  ApiErrorResponse
} from '@libs/data-access/queries'
import {
  restCounterService,
  restService,
  shiftService
} from '@libs/data-access/queries'
import { Hide, Modal, notifications } from '@libs/ui/ds'
import { BaseForm } from '@libs/ui/form'
import i18n, { usePlanningFormTranslation } from '@libs/utils/translations'
import RestTypes, {
  CUSTOM,
  DEFAULT,
  DEFAULT_REST,
  REST_ACCIDENT_ID
} from '@spa/constants/RestTypes'
import { useTrackScheduleFeaturesInteraction } from '@spa/core/externals/segment'
import { type ReduxState } from '@spa/redux/ReduxState'
import { useAppSelector, useMemoSelector } from '@spa/redux/hooks'
import {
  Step,
  resetRestModal,
  updateStep
} from '@spa/scenes/Plannings/Modals/RestModal/redux/modules/main'
import {
  createRests,
  updateRest
} from '@spa/scenes/Plannings/Modals/RestModal/redux/modules/mainSagas'
import {
  getCurrentStep,
  getSubmitResults
} from '@spa/scenes/Plannings/Modals/RestModal/redux/selectors'
import { controllersActions } from '@spa/scenes/Plannings/redux/modules/controllers'
import {
  contractsSelectors,
  getParams
} from '@spa/scenes/Plannings/redux/selectors'
import Immutable from 'immutable'
import {
  CompensatoryRestCounterType,
  RestCounterType
} from 'libs/data-access/queries/src/lib/queries/RestCounter/types/RestCounter'
import { map } from 'lodash-es'
import moment, { type Moment } from 'moment'
import React, { useEffect, useState, type FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAccountFeatureFlags } from 'snap-redux/modules/accounts/hooks'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import {
  getContractById,
  getPlannableContractsByLocationId,
  getPlannableContractsByTeamId
} from 'snap-redux/modules/contracts/selectors'
import { fetchAllMemberships } from 'snap-redux/modules/memberships'
import { getRestById } from 'snap-redux/modules/rests/selectors'
import {
  getShiftsByContractId,
  getShiftsByContractIds
} from 'snap-redux/modules/shifts/selectors'
import { getCurrentUser } from 'snap-redux/modules/users/selectors'

import styles from './RestForm.module.css'
import {
  getSubmitDataBulk,
  getSubmitDataForSingleRest
} from './RestForm.submit'
import { RestFormSection } from './RestFormSection/RestFormSection'
import {
  getRevaluatedData,
  getRevaluatedInputData
} from './RestFormSection/utils'
import { FormError, FormLoading } from './RestSubmitSection'
import { FormConfirmation } from './RestSubmitSection/FormConfirmation/FormConfirmation'
import { usePlannableOverlap, useRestRHF } from './hooks'
import { getContractTimeByWeek, getDefaultRestModel } from './utils'

type RestFormProps = {
  onFormSuccess?: () => void
  restId?: number
  date?: string
  userContractId?: number
  teamId?: number
}

const RestForm: FC<RestFormProps> = ({
  onFormSuccess,
  restId,
  date,
  userContractId,
  teamId
}) => {
  const { t } = usePlanningFormTranslation()
  const scheduleTracker = useTrackScheduleFeaturesInteraction()
  const rest =
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    useMemoSelector(state => getRestById(state, restId)) ||
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    getDefaultRestModel({ date, userContractId })

  const bulkCreateRests = restService.bulk.useCreateList()

  const dispatch = useDispatch()

  const accountFF = useAccountFeatureFlags()

  const account = useSelector(getCurrentAccount)
  const currentUser = useSelector(getCurrentUser)
  const currentUserId = currentUser?.id

  const { formData, methods } = useRestRHF({
    rest
  })

  const { watch } = methods

  const userContractFormValue = formData.user_contract_ids
  const userContractsSize = userContractFormValue.length
  const selectedContractsIds = map(userContractFormValue, 'value')
  const userContractIdFormValue = userContractFormValue.length
    ? selectedContractsIds[0]
    : String(rest.user_contract_id)

  // The contract without the amendments.
  const initialContract = useSelector((state: ReduxState) =>
    userContractIdFormValue
      ? getContractById(state, userContractIdFormValue)
      : null
  )

  // Selected rest part
  const restTypeFormValue = formData.rest_type?.value
  const selectedRest =
    restTypeFormValue && RestTypes.get(String(restTypeFormValue), DEFAULT_REST)
  const selectedRestType = selectedRest?.type
  const selectedRestUsesCounter =
    Boolean(selectedRest?.counter_type) ||
    (selectedRest?.id === 'rtt' && Boolean(accountFF?.enable_rtt))

  const momentRange = moment.range(formData.momentStart, formData.momentEnd)

  // Data part
  const shifts: ShiftRecordMap = useSelector((state: ReduxState) =>
    getShiftsByContractIds(state, selectedContractsIds).filter(shift => {
      const shiftRange = moment.range(shift.starts_at, shift.ends_at)
      return momentRange.overlaps(shiftRange)
    })
  )

  const plannableOverlapExtended = usePlannableOverlap({
    accountId: account?.id,
    rest,
    momentStart: formData.momentStart,
    momentEnd: formData.momentEnd,
    selectedContractsIds,
    restType: restTypeFormValue,
    selectedRestType
  })

  const finalContractsIds =
    plannableOverlapExtended.contract_ids_without_rest_overlap ||
    selectedContractsIds

  const params = useMemoSelector(getParams)

  const contractsData = useAppSelector(state => {
    return Number(teamId)
      ? getPlannableContractsByTeamId(state, Number(teamId))
      : getPlannableContractsByLocationId(state, params.locationId)
  })

  const selectedMemberships = formData.user_contract_ids.reduce<number[]>(
    (acc, next) => {
      const item = contractsData.get(Number(next.value))
      if (item) {
        acc.push(item.membership_id)
      }

      return acc
    },
    []
  )

  const currentStep = useSelector(getCurrentStep)

  const submitData = useSelector(getSubmitResults)
  const startDate = momentRange.start.clone() || moment()
  // We subtract one day on endDate to avoid useless rows issue
  const endDate = momentRange.end.clone().subtract(1, 'd') || moment()

  // Revaluation
  const revaluatedRestValues = rest.custom_value?.toJS() || []
  const [restDurationOption, setRestOption] = useState(
    revaluatedRestValues.length ? CUSTOM : DEFAULT
  )

  const handleRestOptionChange = option => {
    setRestOption(option)
  }

  const completeWeeksRange = moment.range(
    startDate.clone().startOf('isoWeek'),
    endDate.clone().endOf('isoWeek')
  )
  const shiftsInRange = useSelector((state: ReduxState) =>
    getShiftsByContractId(state, userContractIdFormValue).filter(
      ({ starts_at: startsAt, ends_at: endsAt }) => {
        const shiftRange = moment.range(startsAt, endsAt)
        return completeWeeksRange.overlaps(shiftRange)
      }
    )
  )

  const weeksInRange = Immutable.List<Moment>(
    moment
      .range(startDate, endDate.clone().endOf('isoWeek'))
      .by('day', { step: 7 })
  )

  const contract = useMemoSelector(state =>
    contractsSelectors.getCachedById(state, userContractIdFormValue)
  )

  const contractTimeByWeek = getContractTimeByWeek(
    weeksInRange,
    initialContract?.contract_time,
    contract?.amendments
  )

  const revaluatedData = getRevaluatedData(
    weeksInRange,
    contractTimeByWeek,
    account.paid_breaks,
    startDate,
    endDate,
    shiftsInRange
  )

  const initRevaluatedInputData = getRevaluatedInputData(
    revaluatedData,
    revaluatedRestValues
  )
  const [revaluatedInputData, setRevaluatedInputData] = useState(
    initRevaluatedInputData
  )
  const [restCounterOperationDuration, setRestCounterOperationDuration] =
    useState(0)

  const createOperationMutation = restCounterService.operations.useCreateOne({
    onSuccess: () => {
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      onFormSuccess()
      dispatch(
        controllersActions.fetchPlannablesSideEffects({
          contractIds: [userContractIdFormValue],
          date: params.date || moment().format('YYYY-MM-DD'),
          countersParams: { live: true }
        })
      )

      notifications.success({
        message: i18n.t<string>(
          'common.forms:commonForms.forms.rest.create.success'
        )
      })
    },
    onError: error => {
      notifications.error({
        message:
          error?.error?.message ||
          i18n.t<string>('common.notifications:global.genericError')
      })
    }
  })

  /**
   * Handle form submission for creating or updating Rest with rest counter operation
   */
  const submitRestCounter = () => {
    if (selectedRest.id === 'compensatory_rest') {
      const amountToWithdraw = -formData.momentEnd.diff(
        formData.momentStart,
        'minute'
      )
      createOperationMutation.mutate({
        pathParams: { type: RestCounterType.RCR },
        payload: {
          user_contract_id: Number(formData.user_contract_ids[0].value),
          amount_in_minutes: amountToWithdraw,
          rest_attributes: {
            rest_type: CompensatoryRestCounterType.COMPENSATORY_REST,
            starts_at: formData.momentStart.format(),
            ends_at: formData.momentEnd.format(),
            user_contract_id: Number(formData.user_contract_ids[0].value),
            note: watch('note')
          }
        }
      })
    }

    if (selectedRest.id === 'public_holiday_compensatory') {
      const amountToWithdraw = -formData.momentEnd.diff(
        formData.momentStart,
        'minute'
      )
      createOperationMutation.mutate({
        pathParams: { type: RestCounterType.HOLIDAYS },
        payload: {
          user_contract_id: Number(formData.user_contract_ids[0].value),
          amount_in_minutes: amountToWithdraw,
          rest_attributes: {
            rest_type: CompensatoryRestCounterType.PUBLIC_HOLIDAY_REST,
            starts_at: formData.momentStart.format(),
            ends_at: formData.momentEnd.format(),
            user_contract_id: Number(formData.user_contract_ids[0].value),
            note: watch('note')
          }
        }
      })
    }

    if (accountFF?.enable_rtt && selectedRest.id === 'rtt') {
      const restsPayloadData = getSubmitDataForSingleRest({
        currentUserId,
        finalContractsIds,
        formData,
        restDurationOption,
        revaluatedInputData,
        revaluatedData,
        watch
      })[0] as any
      const keysToDelete = ['accident_date']
      keysToDelete.forEach(key => delete restsPayloadData[key])

      createOperationMutation.mutate({
        pathParams: { type: RestCounterType.RTT },
        payload: {
          user_contract_id: Number(formData.user_contract_ids[0].value),
          amount_in_minutes: -(restCounterOperationDuration * 60),
          rest_attributes: restsPayloadData
        }
      })
    }
  }

  useEffect(() => {
    return () => {
      dispatch(resetRestModal())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (teamId) {
      dispatch(
        fetchAllMemberships(account.id, {
          in_progress_on: date,
          perPage: 100,
          teamIds: [teamId]
        } as any)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, dispatch, account.id])

  const planningRefreshByConflictStrategy = () => {
    if (formData.shift_conflict_strategy === 'unassign') {
      shiftService.unassigned.invalidateAllUnassigned()
    }
  }

  /**
   * Handle form submission for creating or updating simple Rest
   */
  const submitRestUpdate = () => {
    const restsPayloadData = getSubmitDataForSingleRest({
      currentUserId,
      finalContractsIds,
      formData,
      restDurationOption,
      revaluatedInputData,
      revaluatedData,
      watch
    }) as any

    dispatch(
      updateRest({
        restId: rest.id,
        rest: restsPayloadData[0],
        overlappingShiftsIds: formData.contracts_ids_shift_overlap,
        shifts,
        onFormSuccess: () => {
          onFormSuccess?.()
          planningRefreshByConflictStrategy()
        }
      })
    )
  }

  const submitRestCreation = () => {
    const restsPayloadData = getSubmitDataForSingleRest({
      currentUserId,
      finalContractsIds,
      formData,
      restDurationOption,
      revaluatedInputData,
      revaluatedData,
      watch
    }) as any

    dispatch(
      createRests({
        rests: restsPayloadData,
        overlappingShiftsIds: formData.contracts_ids_shift_overlap,
        shifts,
        onFormSuccess: () => {
          onFormSuccess?.()
          planningRefreshByConflictStrategy()
        }
      })
    )
  }

  const submitBulkRestCreation = () => {
    const restsPayload = getSubmitDataBulk({
      selectedMemberships,
      formData,
      watch
    })

    const onErrorHandler = (data: ApiErrorResponse) => {
      notifications.error({
        message:
          data.error?.message ||
          i18n.t<string>('common.notifications:global.genericError')
      })
    }

    const onSuccessHandler = () => {
      onFormSuccess?.()
      notifications.success({
        message: i18n.t<string>(
          'common.forms:commonForms.forms.rest.create.success'
        )
      })
      dispatch(controllersActions.planningSilentRefresh(params))
      setTimeout(() => planningRefreshByConflictStrategy(), 1000)
    }

    const onSettledHandler = () => {
      scheduleTracker.restCreationInBulkPerformed(
        plannableOverlapExtended.contracts_only_shift_overlap?.length ?? 0,
        formData.contracts_ids_shift_overlap.length
      )
    }

    bulkCreateRests.mutate(
      {
        payload: restsPayload
      },
      {
        onSuccess: onSuccessHandler,
        onSettled: onSettledHandler,
        onError: onErrorHandler
      }
    )
  }

  /**
   * Dispatch submit to specific handler
   */
  const submitRest = () => {
    if (selectedRestUsesCounter) {
      return submitRestCounter()
    }

    if (rest.id) {
      return submitRestUpdate()
    }

    const isBulk =
      getSubmitDataForSingleRest({
        currentUserId,
        finalContractsIds,
        formData,
        restDurationOption,
        revaluatedInputData,
        revaluatedData,
        watch
      })?.length > 1

    if (isBulk) {
      submitBulkRestCreation()
    } else {
      submitRestCreation()
    }
  }

  /**
   * Handle pre-submit confirmation
   */
  const onPreSubmitRest = () => {
    if (formData.contracts_ids_shift_overlap?.length) {
      return dispatch(updateStep(Step.SUBMIT_CONFIRMATION))
    }
    submitRest()
  }

  return (
    <BaseForm
      methods={methods}
      className={styles.Form}
      onSubmit={onPreSubmitRest}
    >
      {restId && <Modal.Header title={t('restForm.formTitle')} />}
      <Hide
        hide={
          currentStep !== Step.FORM && currentStep !== Step.REVALUATION_FORM
        }
      >
        <RestFormSection
          currentStep={currentStep}
          onSubmit={onPreSubmitRest}
          finalContractsIds={finalContractsIds}
          plannableOverlapExtended={plannableOverlapExtended}
          formData={formData}
          // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
          initialContract={initialContract}
          userContractIdFormValue={userContractIdFormValue}
          // Revalo
          revaluatedData={revaluatedData}
          revaluatedInputData={revaluatedInputData}
          setRevaluatedInputData={setRevaluatedInputData}
          setRestCounterOperationDuration={setRestCounterOperationDuration}
          restDurationOption={restDurationOption}
          handleRestOptionChange={handleRestOptionChange}
          // End revalo
          userContractsSize={userContractsSize}
          selectedRest={selectedRest}
          selectedRestUsesCounter={selectedRestUsesCounter}
          selectedRestType={selectedRestType}
          restTypeFormValue={restTypeFormValue}
          // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
          onFormSuccess={onFormSuccess}
          rest={rest}
          contractsData={contractsData}
          hasAccidentDate={selectedRest?.id === REST_ACCIDENT_ID}
        />
      </Hide>

      {currentStep === Step.SUBMIT_CONFIRMATION && (
        <FormConfirmation
          onCancel={() => {
            dispatch(updateStep(Step.FORM))
          }}
          onSubmit={submitRest}
          selectedContracts={formData.contracts_ids_shift_overlap}
          restType={restTypeFormValue}
          formData={formData}
          isEdit={Boolean(restId)}
        />
      )}

      {currentStep === Step.SUBMIT_LOADING && (
        <FormLoading selectedContracts={formData.user_contract_ids} />
      )}

      {currentStep === Step.SUBMIT_ERROR_FEEDBACK && (
        <FormError
          // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
          contractIdsError={submitData.errorContractIds}
          // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
          restsSuccess={submitData.successResults}
        />
      )}
    </BaseForm>
  )
}

export default RestForm
